export const interests = [
  "romantic",
  "chill_relaxed",
  "party_dancing",
  "upscale_luxurious",
  "casual_friends",
  "live_music",
  "food_focused",
  "culture",
  "outdoor",
  "adventure",
  "wellness",
  "shopping",
  "family_friendly",
] as const;
export type Interest = (typeof interests)[number];
export type DurationPreset =
  | "one_to_two_hours"
  | "three_to_four_hours"
  | "half_day"
  | "full_day";
export type GenerationMode = "standard" | "surprise";
export type BookingMode =
  | "walk_in"
  | "no_booking_required"
  | "request_to_book"
  | "instant_book"
  | "external_booking"
  | "redacted";
export type WeatherAvailability =
  | "available"
  | "unavailable"
  | "stale"
  | "not_requested"
  | "failed";
export type WeatherDecision = "suitable" | "caution" | "unknown" | "redacted";
export type StageStatus =
  | "request_pending"
  | "accepted_deposit_required"
  | "confirmed"
  | "rejected"
  | "replacement_required"
  | "expired"
  | "cancelled";
export type PaymentStatus =
  | "not_required"
  | "required"
  | "pending"
  | "paid"
  | "failed"
  | "expired"
  | "refunded";

export interface City {
  id: string;
  name: string;
  region: string;
  latitude: number;
  longitude: number;
}
export interface Coordinates {
  latitude: number;
  longitude: number;
}
export interface ChildPartyMember {
  ageYears?: number | undefined;
  ageBand?: "infant" | "toddler" | "child" | "teen" | undefined;
}
export interface PlanInput {
  cityId?: string | undefined;
  originLatitude?: number | undefined;
  originLongitude?: number | undefined;
  date: string;
  startTime: string;
  availableDurationMinutes: number;
  adults: number;
  children: number;
  childDetails: ChildPartyMember[];
  maximumPerPersonBudgetMinor?: number | undefined;
  interests: Interest[];
  excludedActivityCategories: string[];
  generationMode: GenerationMode;
  requiresAccessible: boolean;
  requiredAmenities: string[];
}

export interface WeatherContext {
  availability: WeatherAvailability;
  condition: string | null;
  temperatureCelsius: number | null;
  precipitationProbability: number | null;
  sourceTimestamp: string | null;
  requiresRefresh: boolean;
}
export interface RouteMetadata {
  distanceMetres: number;
  durationSeconds: number;
  provider: string;
  calculatedAt: string;
  trafficMode: string;
}
export interface PlanStage {
  id: string;
  stage: number;
  category: string;
  activityOfferingId: string | null;
  branchId: string | null;
  offeringName: string | null;
  venueName: string | null;
  branchName: string | null;
  address: string | null;
  startsAt: string;
  endsAt: string;
  estimatedPerPersonCostMinor: number;
  bookingMode: BookingMode;
  reservationRequired: boolean;
  depositRequired: boolean;
  route: RouteMetadata | null;
  weatherDecision: WeatherDecision;
  reason: string;
  isRedacted: boolean;
  featured: boolean;
  featuredLabel: string | null;
  image?: string | undefined;
  minimumAge?: number | undefined;
  removed?: boolean | undefined;
  rowVersion: number;
}
export interface Plan {
  id: string;
  alternative: number;
  startsAt: string;
  endsAt: string;
  estimatedPerPersonCostMinor: number;
  estimatedPartyCostMinor: number;
  totalRouteDistanceMetres: number;
  totalRouteDurationSeconds: number;
  weather: WeatherContext;
  reason: string;
  featured: boolean;
  featuredLabel: string | null;
  stages: PlanStage[];
  saved?: boolean;
}
export interface PlanResult {
  requestId: string;
  plans: Plan[];
}

export interface VenueSummary {
  id: string;
  venueName: string;
  branchName: string;
  address: string;
  area: string;
  latitude: number;
  longitude: number;
  priceBand: 1 | 2 | 3 | 4;
  activityCategories: string[];
  bookingModes: BookingMode[];
  hasRequestToBook: boolean;
  interests: Interest[];
  rating: number;
  image: string;
  isOpen: boolean;
  featured?: boolean;
}
export interface VenueDetails extends VenueSummary {
  description: string;
  minimumAge: number;
  depositMinor: number;
  currency: "USD";
  phone: string;
  openingHours: string[];
  amenities: string[];
  packages: string[];
  cancellationPolicy: string;
}
export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: string;
}
export interface ReservationStage {
  venueRequestId: string;
  venueName: string;
  branchName: string;
  startsAt: string;
  bookingMode: BookingMode;
  status: StageStatus;
  paymentStatus: PaymentStatus;
  depositMinor: number;
  currency: "USD";
  replacementWorkflowId?: string;
}
export interface Reservation {
  id: string;
  status: string;
  requestedAt: string;
  walkInStages: PlanStage[];
  stages: ReservationStage[];
}
export interface VenueReservationInput {
  venueId: string;
  requestedLocalDate: string;
  requestedLocalTime: string;
  partySize: number;
  leadGuestName: string;
  verifiedMobile: string;
  occasion?: string;
  specialRequests?: string;
}
export interface NotificationItem {
  id: string;
  kind: string;
  title: string;
  body: string;
  createdAt: string;
  read: boolean;
  deepLink?: string;
}
export interface ApiProblem {
  status: number;
  title: string;
  detail?: string;
  errors?: Record<string, string[]>;
  correlationId?: string;
}
